import React, { useRef } from 'react'
import { useRouter } from 'next/router'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import { AccountComplete, Login } from 'config/constants'
import { CaptchaType, useSignupMutation } from 'types/domain'
import { graphqlErrorCatch } from 'utils'
import { AnchorLink } from 'components/ui'
import { SignupFormProvider, SignupFormValues, useSignupForm } from '../formContext'
import {
  AuthenticationFormValues,
  CaptchaCodeSendAndInput,
  IdentifierInput,
  useFormValidations,
} from 'components/business'
import { UseFormReturnType } from '@mantine/form/lib/types'
import { getHxAccountReferer, getHxUtmMedium, getHxUtmSource } from 'utils/cookieUtils'
import { Button, Stack, Title } from '@mantine/core'
import Agreement, { AgreementApi } from 'modules/Signup/components/Agreement'
import PasswordInput from './PasswordInput'
import { LOGIN_IDENTIFIER } from 'modules/Login/constants'
import { initAndCleanUpLoginData } from 'modules/Login/actionsAfterLogin'
import useCachedIdentifier from 'components/business/hooks/useCachedIdentifier'

const SignupForm = () => {
  const router = useRouter()
  const { t } = useTranslation('pageSignup')
  const { signupFormValidate } = useFormValidations()
  const [signupMutation, { loading: submitting }] = useSignupMutation()
  const agreementRef = useRef<AgreementApi>(null)
  const form = useSignupForm({
    initialValues: {
      identifier: '',
      password: '',
      confirmPassword: '',
      captcha: '',
      agreement: false,
      accountName: t('form.defaultAccountName'),
    },
    validateInputOnBlur: ['identifier'],
    validate: (values) => {
      return signupFormValidate(values, form)
    },
  })

  useCachedIdentifier(form)

  const handleSignup = async (values: SignupFormValues) => {
    const { identifier, password, captcha, accountName, agreement } = values
    if (!agreement) {
      return agreementRef.current?.setTooltipOpened(true)
    }
    try {
      const { data } = await signupMutation({
        variables: {
          input: {
            identifier: identifier.trim(),
            password,
            captcha,
            accountName: accountName.trim(),
            adSource: getHxUtmSource(),
            adWord: getHxUtmMedium(),
            referer: getHxAccountReferer(),
          },
        },
      })
      if (data?.signUpUser) {
        const { token, user } = data.signUpUser
        initAndCleanUpLoginData(token!, user?.id!)
        await router.replace(AccountComplete)
      }
    } catch (err: any) {
      const { graphQLErrors } = err
      handleErrors(graphQLErrors)
    }
  }

  const identifierExistedErrorHandler = () => {
    const { identifier } = form.values
    const message = (
      <Trans
        i18nKey="pageSignup:errors.identifierExisted"
        components={{
          component: <AnchorLink href={Login} />,
        }}
      />
    )
    form.setFieldError('identifier', message)
    sessionStorage.setItem(LOGIN_IDENTIFIER, identifier)
  }

  const handleErrors = (graphQLErrors: any) => {
    const errorCode = graphQLErrors?.[0]?.extensions?.code
    if (errorCode == 'error.code_conflict') {
      identifierExistedErrorHandler()
    } else {
      graphqlErrorCatch({
        graphQLErrors,
        mapping: new Map([['error.mismatched', t('user:authentication.form.errors.captchaMismatched')]]),
        defaultMessage: t('errors.default'),
      })
    }
  }

  return (
    <SignupFormProvider form={form}>
      <form onSubmit={form.onSubmit(handleSignup)} className="h-full">
        <Stack spacing="xl">
          <Title order={2}>{t('desc')}</Title>
          <Stack spacing="xl">
            <IdentifierInput
              size="md"
              required
              formContext={form as UseFormReturnType<AuthenticationFormValues | SignupFormValues>}
            />
            <CaptchaCodeSendAndInput
              captchaType={CaptchaType.SignUp}
              form={form as never}
              identifierExistedErrorHandler={identifierExistedErrorHandler}
            />
            <PasswordInput />
            <Button
              size="md"
              fullWidth
              loading={submitting}
              disabled={
                !form.values.identifier || !form.isValid('identifier') || !form.values.password || !form.values.captcha
              }
              type="submit"
            >
              {t('form.submit')}
            </Button>
            <Agreement ref={agreementRef} />
          </Stack>
        </Stack>
      </form>
    </SignupFormProvider>
  )
}

export default SignupForm
